import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Mail, MailOpen, ShieldCheck } from "lucide-react";
import { listMyInbox } from "@/lib/notifications.functions";

const inboxQuery = queryOptions({ queryKey: ["inbox"], queryFn: () => listMyInbox() });

export const Route = createFileRoute("/_authenticated/inbox")({
  head: () => ({
    meta: [
      { title: "Inbox — Shields" },
      { name: "description", content: "Every email Shields has sent you, in one place." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InboxPage,
});

function InboxPage() {
  const { data: items } = useSuspenseQuery(inboxQuery);
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);
  const open = items.find((i) => i.id === openId) ?? null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="section-pad mx-auto flex h-14 max-w-5xl items-center gap-3">
          <Link to="/customer/more" className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" aria-hidden /> More
          </Link>
          <span className="ml-auto flex items-center gap-2 text-sm font-bold">
            <Mail className="h-4 w-4 text-primary" aria-hidden /> Inbox
          </span>
        </div>
      </header>

      <main className="section-pad mx-auto max-w-5xl py-8">
        <h1 className="font-display text-2xl font-extrabold">Your Shields mail</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Booking confirmations, quotes and updates — exactly what we emailed you.
        </p>

        {items.length === 0 ? (
          <section className="mt-8 rounded-xl border border-dashed border-border p-8 text-center">
            <Mail className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
            <h2 className="mt-3 font-display text-lg font-bold">No Shields mail yet</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Booking confirmations and important service updates will appear here.
            </p>
            <Link to="/book" className="btn-hero mt-5 inline-flex">
              Book a service
            </Link>
          </section>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <ul className="space-y-1.5">
              {items.map((i) => (
                <li key={i.id}>
                  <button
                    onClick={() => setOpenId(i.id)}
                    className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                      i.id === openId
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {i.readAt ? (
                        <MailOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      ) : (
                        <Mail className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                      )}
                      <p className="truncate text-sm font-bold">{i.subject}</p>
                    </div>
                    <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{i.body}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {new Date(i.createdAt).toLocaleString("en-GB")}
                    </p>
                  </button>
                </li>
              ))}
            </ul>

            {open && (
              <article className="card-surface p-6">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                  <ShieldCheck className="h-4 w-4" aria-hidden /> Shields
                </div>
                <h2 className="mt-3 font-display text-xl font-extrabold">{open.title}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(open.createdAt).toLocaleString("en-GB")}
                </p>
                <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{open.body}</p>
                {open.details.length > 0 && (
                  <dl className="mt-4 space-y-1 rounded-xl bg-muted/60 p-4 text-sm">
                    {open.details.map((d, idx) => (
                      <div key={idx} className="flex gap-2">
                        {d.label && <dt className="font-bold">{d.label}:</dt>}
                        <dd>{d.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {open.ctaText && open.ctaUrl && (
                  <a
                    href={open.ctaUrl}
                    className="btn-hero mt-6 inline-flex"
                  >
                    {open.ctaText}
                  </a>
                )}
              </article>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
